import { makeStyles, tokens, Text, Badge } from '@fluentui/react-components';
import type { MaturityLevel } from '../types';
import { getScoreHex } from '../utils/scoring';

interface MaturityScoreGaugeProps {
  score: number;
  level: MaturityLevel;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
  },
  svgContainer: {
    position: 'relative',
    width: '180px',
    height: '120px',
  },
  scoreOverlay: {
    position: 'absolute',
    top: '60px',
    left: '50%',
    transform: 'translateX(-50%)',
    textAlign: 'center',
  },
  scoreValue: {
    fontSize: '36px',
    fontWeight: 700,
    lineHeight: '1',
  },
});

export function MaturityScoreGauge({ score, level }: MaturityScoreGaugeProps) {
  const styles = useStyles();
  const color = getScoreHex(score);

  // SVG arc for a semicircle gauge
  const cx = 90;
  const cy = 90;
  const r = 70;
  const strokeWidth = 12;
  const circumference = Math.PI * r; // half circle
  const progress = (score / 100) * circumference;

  // Arc path (semicircle from left to right)
  const startX = cx - r;
  const startY = cy;
  const endX = cx + r;
  const endY = cy;

  const bgPath = `M ${startX} ${startY} A ${r} ${r} 0 0 1 ${endX} ${endY}`;

  return (
    <div className={styles.container}>
      <div className={styles.svgContainer}>
        <svg width="180" height="120" viewBox="0 0 180 120">
          {/* Background arc */}
          <path
            d={bgPath}
            fill="none"
            stroke={tokens.colorNeutralStroke2}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          {/* Progress arc */}
          <path
            d={bgPath}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${progress} ${circumference}`}
          />
        </svg>
        <div className={styles.scoreOverlay}>
          <div className={styles.scoreValue} style={{ color }}>
            {score}
          </div>
        </div>
      </div>
      <Badge
        appearance="filled"
        color={score > 70 ? 'success' : score >= 40 ? 'warning' : 'danger'}
        size="medium"
      >
        {level}
      </Badge>
      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
        out of 100
      </Text>
    </div>
  );
}
